import { FormFields, FormHeader, FormSection } from '../form';
import { YesNoButton, Input } from '../../../../components';
import { useValidation } from '../../../../hooks';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';

import type { Dispatch, FC, SetStateAction } from 'react';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

interface ISpanReplacementProps {
  setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
}

export const SpanReplacement: FC<ISpanReplacementProps> = ({ setSaveAndQuitChanges }) => {
  const formContext = useFormContext();
  const { fieldRequirements } = useValidation({});
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const isDamageUnderground: boolean | null = formContext.watch(claimFields.wasDamageUnderground);
  const isSpanReplacementNeeded: boolean | null = formContext.watch(claimFields.wasSpanReplaced);
  const isSpanFootageReplacedUnknown: boolean = formContext.watch(claimFields.isSpanFootageUnknown);
  const isSpanOtherMaterialUnknown: boolean = formContext.watch('isSpanOtherMaterialUnknown');
  const isSpanUgCableInConduit: boolean | null = formContext.watch(claimFields.cableInConduit);

  const onIsSpanReplacementNeededYesClick = () => {
    setSaveAndQuitChanges?.((prev) => [
      ...prev,
      claimFields.cableInConduit,
      claimFields.cableSize,
      claimFields.isSpanFootageUnknown,
      claimFields.isSpanReplacementNeeded,
      claimFields.spanFootage,
      claimFields.wasSpanReplaced,
    ]);
    formContext.setValue(claimFields.wasSpanReplaced, true);

    if (formContext.getValues(claimFields.wasDamageUnderground) === false) {
      formContext.setValue(claimFields.cableInConduit, false);
    }

    formContext.clearErrors(claimFields.wasSpanReplaced);
    formContext.clearErrors(claimFields.cableInConduit);
    formContext.clearErrors(claimFields.spanFootage);
    formContext.clearErrors(claimFields.isSpanFootageUnknown);
    formContext.clearErrors(claimFields.cableSize);
    formContext.clearErrors('isSpanOtherMaterialUnknown');
  };

  const onIsSpanReplacementNeededNoClick = () => {
    setSaveAndQuitChanges?.((prev) => [
      ...prev,
      claimFields.cableInConduit,
      claimFields.cableSize,
      claimFields.isSpanFootageUnknown,
      claimFields.isSpanReplacementNeeded,
      claimFields.spanFootage,
      claimFields.wasSpanReplaced,
    ]);
    formContext.setValue(claimFields.wasSpanReplaced, false);
    formContext.setValue(claimFields.cableInConduit, null);
    formContext.setValue(claimFields.spanFootage, '');
    formContext.setValue(claimFields.isSpanFootageUnknown, false);
    formContext.setValue(claimFields.cableSize, '');
    formContext.setValue('isSpanOtherMaterialUnknown', false);
    formContext.clearErrors(claimFields.cableInConduit);
    formContext.clearErrors(claimFields.wasSpanReplaced);
    formContext.clearErrors(claimFields.spanFootage);
    formContext.clearErrors(claimFields.isSpanFootageUnknown);
    formContext.clearErrors(claimFields.cableSize);
    formContext.clearErrors('isSpanOtherMaterialUnknown');
  };

  return (
    <FormSection>
      <FormHeader>{localization.translate('spanReplacement.header')}</FormHeader>

      <FormFields>
        {/* WAS SPAN REPLACEMENT NEEDED? */}
        <YesNoButton
          isRequired={fieldRequirements.WasSpanReplaced}
          isSelected={isSpanReplacementNeeded}
          labelText={localization.translate('spanReplacement.wasSpanReplacementNeededLabel')}
          name={claimFields.wasSpanReplaced}
          onNoClick={onIsSpanReplacementNeededNoClick}
          onYesClick={onIsSpanReplacementNeededYesClick}
          tooltip={localization.translate('spanReplacement.wasSpanReplacementNeededTooltip')}
        />

        {isSpanReplacementNeeded !== false && (
          <>
            {/* WAS CABLE IN CONDUIT? */}
            {isDamageUnderground && (
              <YesNoButton
                isRequired={fieldRequirements.CableInConduit}
                isSelected={isSpanUgCableInConduit}
                labelText={localization.translate('spanReplacement.wasCableInConduitLabel')}
                name={claimFields.cableInConduit}
                onNoClick={() => formContext.setValue(claimFields.cableInConduit, false)}
                onYesClick={() => formContext.setValue(claimFields.cableInConduit, true)}
                tooltip={localization.translate('spanReplacement.wasCableInConduitTooltip')}
              />
            )}

            {/* FOOTAGE DAMAGE/REPLACED */}
            <Input
              checkboxOptions={{
                'aria-label': localization.translate('spanReplacement.spanFootageUnknownCheckbox'),
                isSelected: isSpanFootageReplacedUnknown,
                onChange: (checked) =>
                  formContext.setValue(claimFields.isSpanFootageUnknown, checked),
              }}
              inputOptions={{
                isDisabled: isSpanFootageReplacedUnknown,
                isRequired: fieldRequirements.SpanFootage,
                label: localization.translate('spanReplacement.spanFootageLabel'),
                name: claimFields.spanFootage,
                onChange: () => formContext.setValue(claimFields.isSpanReplacementNeeded, true),
              }}
              tooltip={localization.translate('spanReplacement.spanFootageTooltip')}
              type={'number'}
            />

            {/* CABLE SIZE/ FIBER COUNT / OTHER */}
            <Input
              checkboxOptions={{
                'aria-label': localization.translate('spanReplacement.cableSizeUnknownCheckbox'),
                isSelected: isSpanOtherMaterialUnknown,
                onChange: (checked) =>
                  formContext.setValue('isSpanOtherMaterialUnknown', checked),
              }}
              inputOptions={{
                isDisabled: isSpanOtherMaterialUnknown,
                isRequired: fieldRequirements.CableSize,
                label: localization.translate('spanReplacement.cableSizeLabel'),
                name: claimFields.cableSize,
                onChange: () => formContext.setValue(claimFields.isSpanReplacementNeeded, true),
              }}
              tooltip={localization.translate('spanReplacement.cableSizeTooltip')}
            />
          </>
        )}
      </FormFields>
    </FormSection>
  );
};